import redirectInterceptor from "@/interceptors/redirect_interceptor";
import VoyageOptionsData from "../../domain/weather_routing/voyage_options/data";
import CreateVoyageData from "../../domain/weather_routing/create_voyage/data";
import createVoyageDataToJson from "../../domain/weather_routing/create_voyage/mapper_to_json";
import vesselMetricsDataFromJson from "../../domain/weather_routing/vessel_metrics/mapper";
import WeatherRoutingVoyageService from "../../services/voyage/service";
import HttpError from "../../services/http_error";
import WeatherRoutingRouteService from "../../services/route/service";
import customRouteFromJson from "../../domain/weather_routing/custom_route/mapper";
import createRouteDataToJson from "../../domain/weather_routing/create_route/mapper_to_json";
import selectVesselPlugin from "../../helper_functions/select_vessel_plugin";
import voyageDataFromJson from "../../domain/weather_routing/voyage/db/mapper";
import VoyageFuelCreateData from "../../domain/weather_routing/voyage_options/fuel/create_data";
import VoyageConstraintsCreateData from "../../domain/weather_routing/voyage_options/constraints/create_data";
import VoyageArrivalWindowCreateData from "../../domain/weather_routing/voyage_options/arrival_window/create_data";
import voyageComputationStateFromJson from "../../domain/weather_routing/calculation_state/db/mapper";
import TimedIntervalManager from "../../helper_functions/timed_interval_interface";
const localWeatherRoutingServerUrl = process.env.VUE_APP_LOCAL_WR_API_URL;
const companyId = process.env.VUE_APP_COMPANY_ID;
const timedIntervalManager = new TimedIntervalManager();

const basicUrl = process.env.VUE_APP_BASIC_URL;

const voyageService = new WeatherRoutingVoyageService(
  localWeatherRoutingServerUrl,
  companyId,
  createVoyageDataToJson,
  voyageDataFromJson,
  voyageComputationStateFromJson
);

const routeService = new WeatherRoutingRouteService(
  localWeatherRoutingServerUrl,
  companyId,
  customRouteFromJson,
  createRouteDataToJson
);

const getDefaultState = () => {
  const timeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };

  return {
    useCurrentVesselPosition: true,
    selectedFromPort: null,
    selectedToPort: null,
    voyageDescription: "",
    serviceSpeed: 12,
    speedMargin: 0.5,
    efficiency: 2.5,
    draft: 10,
    trim: 0,
    vesselPlugin: "com.theyr.vos.vessel.tabular.5D2",
    voyageDate: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10),
    voyageTime: new Date().toLocaleTimeString("en-GB", timeFormatOptions),
    etdWindow: 0,
    justInTimeArrival: false,
    enforceArrivalWindow: false,
    arrivalDate: null,
    arrivalTime: null,
    earliestDate: null,
    earliestTime: null,
    latestDate: null,
    latestTime: null,
    calculationState: "",
    ecaFuelPrice: 600,
    nonEcaFuelPrice: 500,
    dailyCosts: 10000,
    ecaFuelEnergy: 40.5,
    nonEcaFuelEnergy: 40.5,
    maxWindCruiseSpeed: 39,
    maxWindServiceSpeed: 41,
    maxWaveCruiseHeight: 6,
    maxWaveServiceHeight: 8,
    landProximity: 2,
    tss: true,
    eca: true,
    avoidPiracy: true,
    avoidTropicalStorms: true,
    useSuez: true,
    useKiel: false,
    usePanama: true,
    useTaiwanStrait: true,
    selectedRoute: null,
    selectedNoGoAreas: [],
    useSelectedRoute: false,

    currentTab: 0,

    calculationLoading: false,
    calculationError: null,
  };
};

const state = getDefaultState();

const getters = {
  getCalculationState: (state) => (state.calculationState),
  getThereIsVoyageData: (state) => {
    return state.voyageRoutes !== null;
  },
  calculationFailed: (state) => (state.calculationError !== null),
};

const actions = {

  async fetchCurrentVesselMetrics({ commit }, payload) {

    const vesselName = payload.vesselName;
    const response = await redirectInterceptor.get(
      `${basicUrl}/weather-routing/vessel/${vesselName}/metrics`,
      {
        headers: {
          Authorization: localStorage.getItem("quad_token")
        }
      }
    );

    if (response.status >= 200 && response.status < 400) {
      const vesselMetrics = vesselMetricsDataFromJson(response.data);
      commit("SET_DRAFT", vesselMetrics.meanDraft);
      commit("SET_TRIM", vesselMetrics.trim);
      commit("SET_EFFICIENCY", 0);//vesselMetrics.fouling)
    }
  },

  async createRoute({ state, rootState }) {
    const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
    const currentVesselCoordinates = rootState.weatherRouting.currentVesselCoordinates;

    const response = state.useCurrentVesselPosition
      ? await routeService.postSimpleRoute(
        localStorage.getItem("quad_token"),
        theyrToken,
        currentVesselCoordinates,
        null,
        state.selectedToPort
      )
      : await routeService.postSimpleRoute(
        localStorage.getItem("quad_token"),
        theyrToken,
        null,
        state.selectedFromPort,
        state.selectedToPort
      );

    return response;
  },

  async createVoyage({ state, rootState }, payload) {
    const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
    const vesselParticulars = rootState.weatherRouting.vesselParticulars;
    const routeId = payload.routeId;
    const routeTheyrId = payload.routeTheyrId;
    const vesselId = payload.vesselId;
    const vesselTheyrId = payload.vesselTheyrId;

    const voyageOptions = _setUpVoyageOptions(vesselParticulars);

    const createVoyage = new CreateVoyageData(
      routeId,
      routeTheyrId,
      vesselId,
      vesselTheyrId,
      companyId,
      state.voyageDescription,
      `${state.voyageDate}T${state.voyageTime}`,
      voyageOptions
    );

    const voyage = await voyageService.post(
      localStorage.getItem("quad_token"),
      theyrToken,
      createVoyage
    );
    return voyage;
  },

  async pushVoyageCalculation({ commit, dispatch, rootState, rootGetters }, payload) {
    commit("SET_CALCULATION_LOADING");
    let selectedVessel;
    try {
      selectedVessel = selectVesselPlugin(
        Number(state.trim),
        Number(state.efficiency),
        rootState.weatherRouting.vessels,
        rootGetters["weatherRouting/getTrimValues"],
        rootGetters["weatherRouting/getFoulingValues"],
        rootState.weatherRouting.vesselParticulars.IMO
      );
    } catch (e) {
      commit("SET_CALCULATION_FAILED", "Vessel selection failed");
    }

    if (selectedVessel === undefined) {
      commit("SET_CALCULATION_FAILED", "No vessel with given parameters found");
      return;
    }

    try {
      const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;

      const route = state.useSelectedRoute
        ? state.selectedRoute
        : await dispatch("createRoute");

      try {
        const voyage = await dispatch("createVoyage", {
          "routeId": route.id,
          "routeTheyrId": route.theyrId,
          "vesselId": selectedVessel.id,
          "vesselTheyrId": selectedVessel.theyrId,
        });

        const computation = await voyageService.postVoyageCalculation(
          localStorage.getItem("quad_token"),
          theyrToken,
          voyage.theyrId
        );

        timedIntervalManager.createInterval(
          async () => {
            try {
              const calculationState = await dispatch(
                "checkCalculationState",
                { "computationId": computation.id }
              );
              commit("SET_CALCULATION_STATE", calculationState);
              // should also add a timeout check
              if (calculationState.stateValue === 0) {
                timedIntervalManager.clearAll();
                const callbackFunction = payload.callback;
                await dispatch(
                  "checkCalculationState",
                  { "computationId": computation.id }
                );
                callbackFunction(voyage.theyrId);
                commit("SET_CALCULATION_SUCCESS");
              }
              else if (calculationState.stateValue < 0) {
                commit("SET_CALCULATION_FAILED", calculationState.stateTitle);
                timedIntervalManager.clearAll();
              }
            } catch (e) {
              if (e instanceof HttpError) {
                commit("SET_CALCULATION_FAILED", `${e.message}`);
                timedIntervalManager.clearAll();
                return;
              }
            }
          },
          1500,
          600000,
          () => {
            timedIntervalManager.clearAll();
            commit("SET_CALCULATION_FAILED", "Computation time exceeded");
          }
        );
      } catch (e) {
        if (e instanceof HttpError) {
          commit("SET_CALCULATION_FAILED", `${e.message}`);
          return;
        }
        commit("SET_CALCULATION_FAILED", `${e}`);
        return;
      }

    } catch (e) {
      commit("SET_CALCULATION_FAILED", `${e}`);
      return;
    }
  },

  async checkCalculationState({ rootState }, payload) {
    const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
    const computationId = payload.computationId;
    const response = await voyageService.getVoyageCalculationState(
      localStorage.getItem("quad_token"),
      theyrToken,
      computationId
    );
    return response;

  },
};

const mutations = {
  SET_SELECTED_FROM_PORT: (state, data) => (state.selectedFromPort = data),
  SET_SELECTED_TO_PORT: (state, data) => (state.selectedToPort = data),
  SET_USE_CURRENT_VESSEL_POSITION: (state, data) => (state.useCurrentVesselPosition = data),
  SET_VOYAGE_DESCRIPTION: (state, data) => (state.voyageDescription = data),
  SET_SERVICE_SPEED: (state, data) => (state.serviceSpeed = data),
  SET_EFFICIENCY: (state, data) => (state.efficiency = data),
  SET_SELECTED_DATE: (state, data) => (state.voyageDate = data),
  SET_SELECTED_TIME: (state, data) => (state.voyageTime = data),
  SET_VOYAGE_ROUTE: (state, data) => (state.voyageRoutes = data),
  SET_CALCULATION_STATE: (state, data) => (state.calculationState = data),
  SET_DRAFT: (state, data) => (state.draft = data),
  SET_TRIM: (state, data) => (state.trim = data),
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_JUST_IN_TIME_ARRIVAL: (state, data) => (state.justInTimeArrival = data),
  SET_ENFORCE_ARRIVAL_WINDOW: (state, data) => (state.enforceArrivalWindow = data),
  SET_ARRIVAL_DATE: (state, data) => (state.arrivalDate = data),
  SET_ARRIVAL_TIME: (state, data) => (state.arrivalTime = data),
  SET_EARLIEST_DATE: (state, data) => (state.earliestDate = data),
  SET_EARLIST_TIME: (state, data) => (state.earliestTime = data),
  SET_LATEST_DATE: (state, data) => (state.latestDate = data),
  SET_LATEST_TIME: (state, data) => (state.latestTime = data),
  SET_ETD_WINDOW: (state, data) => (state.etdWindow = data),
  SET_SPEED_MARGIN: (state, data) => (state.speedMargin = data),
  SET_ECA_FUEL_PRICE: (state, data) => (state.ecaFuelPrice = data),
  SET_NON_ECA_FUEL_PRICE: (state, data) => (state.nonEcaFuelPrice = data),
  SET_DAILY_COSTS: (state, data) => (state.dailyCosts = data),
  SET_MAX_WIND_CRUISE_SPEED: (state, data) => (state.maxWindCruiseSpeed = data),
  SET_MAX_WIND_SERVICE_SPEED: (state, data) => (state.maxWindServiceSpeed = data),
  SET_MAX_WAVE_CRUISE_HEIGHT: (state, data) => (state.maxWaveCruiseHeight = data),
  SET_MAX_WAVE_SERVICE_HEIGHT: (state, data) => (state.maxWaveServiceHeight = data),
  SET_LAND_PROXIMITY: (state, data) => (state.landProximity = data),
  SET_TSS: (state, data) => (state.tss = data),
  SET_ECA: (state, data) => (state.eca = data),
  SET_AVOID_PIRACY: (state, data) => (state.avoidPiracy = data),
  SET_AVOID_TROPICAL_STORMS: (state, data) => (state.avoidTropicalStorms = data),
  SET_USE_SUEZ: (state, data) => (state.useSuez = data),
  SET_USE_KIEL: (state, data) => (state.useKiel = data),
  SET_USE_PANAMA: (state, data) => (state.usePanama = data),
  SET_USE_TAIWAN_STRAIT: (state, data) => (state.useTaiwanStrait = data),
  SET_ECA_FUEL_ENERGY: (state, data) => (state.ecaFuelEnergy = data),
  SET_NON_ECA_FUEL_ENERGY: (state, data) => (state.nonEcaFuelEnergy = data),
  SET_CURRENT_TAB: (state, data) => (state.currentTab = data),
  SET_SELECTED_ROUTE: (state, data) => (state.selectedRoute = data),
  SET_USE_SELECTED_ROUTE: (state, data) => (state.useSelectedRoute = data),
  SET_SELECTED_NO_GO_AREAS: (state, data) => (state.selectedNoGoAreas = data),
  SET_CALCULATION_LOADING(state) {
    state.calculationError = null;
    state.calculationLoading = true;
    state.calculationState = "";
  },
  SET_CALCULATION_FAILED(state, data) {
    state.calculationLoading = false;
    state.calculationError = data;
    state.calculationState = "";
  },
  SET_CALCULATION_SUCCESS(state) {
    state.calculationLoading = false;
    state.calculationError = null;
    state.calculationState = "";
  },
  CLEAR_INTERVAL(state) {
    state.calculationLoading = false;
    state.calculationError = null;
    state.calculationState = "";
    timedIntervalManager.clearAll();
  }
};

export const weatherRoutingFormModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

function _setUpVoyageOptions(vesselParticulars) {
  const voyageOptions = new VoyageOptionsData(
    Number(state.serviceSpeed) + Number(state.speedMargin),
    state.serviceSpeed - state.speedMargin,
    Number(state.serviceSpeed) + Number(state.speedMargin),
    state.serviceSpeed - state.speedMargin,
  );
  voyageOptions.vesselDraft = Math.round(state.draft * 100) / 100;
  voyageOptions.dailyRate = state.dailyCosts;
  voyageOptions.maxServicePower = 1.15 * vesselParticulars.MCR;
  voyageOptions.maxServiceRpm = 1.15 * vesselParticulars.nMCR;
  voyageOptions.useEca = state.eca;
  voyageOptions.avoidPiracy = state.avoidPiracy;
  voyageOptions.usePanama = state.usePanama;
  voyageOptions.useSuez = state.useSuez;
  voyageOptions.useKiel = state.useKiel;
  voyageOptions.useTaiwanStrait = state.useTaiwanStrait;
  voyageOptions.etdWindow = state.etdWindow;
  voyageOptions.landProximity = state.landProximity;
  voyageOptions.useTss = state.tss;
  voyageOptions.fuel =
        [
          new VoyageFuelCreateData(
            state.nonEcaFuelPrice,
            state.nonEcaFuelEnergy,
            "",
            0
          ),
          new VoyageFuelCreateData(
            state.ecaFuelPrice,
            state.ecaFuelEnergy,
            "",
            0
          ),
        ];
  voyageOptions.constraints = new VoyageConstraintsCreateData(
    null,
    state.maxWindServiceSpeed,
    state.maxWindCruiseSpeed,
    state.maxWaveServiceHeight,
    state.maxWaveCruiseHeight
  );
  if (state.justInTimeArrival) {
    voyageOptions.dta = `${state.arrivalDate}T${state.arrivalTime}`;
  }
  else {
    if (state.enforceArrivalWindow) {
      voyageOptions.arrivalWindow = new VoyageArrivalWindowCreateData(
        `${state.earliestDate}T${state.earliestTime}`,
        `${state.latestDate}T${state.latestTime}`,
        state.enforceArrivalWindow
      );
    }

  }

  return voyageOptions;
}
